// composables/useApiService.js
import { inject } from 'vue';
import { useLoading } from 'vue-loading-overlay';

export function useApiService() {

  const router = inject('router');
  const cookies = inject('cookies');
  const notification = inject('notificationService');

  const loading = useLoading();
  let loaderHolder = null;

  const loaderOptions = {
    loader: 'dots',
    color: '#3f4f9f',
    width: 100,
    height: 100,
    backgroundColor: '#ffffff',
    zIndex: 999
  };

  function extractToken(response) {
    const csrfToken = response.headers.get('X-CSRF-TOKEN');
    if (csrfToken) {
      cookies.set("X-CSRF-TOKEN", csrfToken);
    }
  }

  function getBaseUrl(url) {
      let baseUrl = `${window.location.origin}/api/`;
    // let baseUrl = `${window.location.origin}/`;
    return `${baseUrl}${url}`;
  }

  function showLoader() {
    hideLoader();
    loaderHolder = loading.show(loaderOptions);
  }

  function hideLoader() {
    if (loaderHolder) {
      loaderHolder.hide();
      loaderHolder = null;
    }
  }

  function getJSON(data) {
    return data != null && data !== ""
      ? JSON.parse(data, (key, value) => {
        const regEx = /^(-?(?:[1-9][0-9]*)?[0-9]{4})-(1[0-2]|0[1-9])-(3[01]|0[1-9]|[12][0-9])T(2[0-3]|[01][0-9]):([0-5][0-9]):([0-5][0-9])(.[0-9]+)?(Z)?$/;
        // if (regEx.test(value)) return moment(value)._d;
        return value;
      })
      : data;
  }

  function parserJsonResponse(response) {
    return response.text().then(data => getJSON(data));
  }

  function parseBlobResponse(response, contentType) {
    if (response.body && !response.bodyUsed) {
      let fileName = response.headers.get('Content-Disposition')?.split(';')[1]?.trim().split('=')[1];
      response.blob().then(data => {
        const fileBlob = new Blob([data], { type: contentType });
        saveAs(fileBlob, fileName);
      });
    }
    return null;
  }

  async function handleResponse(response, expectedStatusCode, responseHandler, contentType = null, shouldNotifySuccess = true, successFunc = () => { }, shouldNotifyFailure = true, okButtonCallback = () => { }) {
    if (response.status === expectedStatusCode) {
      if (shouldNotifySuccess && successFunc) successFunc();
      return responseHandler(response, contentType);
    } else if (response.status === 401 || response.status === 403) {
      if (router.currentRoute.value.path !== '/login') {
        const messageObject = await responseHandler(response, contentType);
        if (messageObject) {
          notification.notifyError(messageObject.Reason, null, () => router.push(`/login`));
        } else {
          router.push(`/login`);
        }
        return Promise.reject();
      } else if (response.status === 403) {
        router.push(`/`);
      }
    } else {
      if (response.status === 422) {
        throw { errorCode: 422, errorMessage: "Cannot process your request as it either may have expired or may not meet the specified conditions." };
      }

      if (shouldNotifyFailure) {
        const errorMessage = await response.text();
        if (response.status >= 500) {
          notification.notifyError(errorMessage || response.statusText, okButtonCallback);
        } else {
          notification.notifyInfoError(errorMessage || response.statusText, okButtonCallback);
        }
      }
    }

    return null;
  }

  function handleJsonResponse(response, expectedStatusCode, ...args) {
    return handleResponse(response, expectedStatusCode, parserJsonResponse, null, ...args);
  }

  function handleBlobResponse(response, expectedStatusCode, contentType, ...args) {
    return handleResponse(response, expectedStatusCode, parseBlobResponse, contentType, ...args);
  }

  function fetchWrapperForPost(url, content, shouldNotifySuccess = true, shouldNotifyFailure = true, shouldShowLoader = false, shouldHandleResponse = true, callBackEvent = () => { }) {

    if (shouldShowLoader) showLoader();
    url = getBaseUrl(url);

    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(content),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': cookies.get("X-CSRF-TOKEN")
      }
    }).then(response => {
      extractToken(response);
      if (shouldShowLoader) hideLoader();
      return shouldHandleResponse
        ? handleJsonResponse(response, 201, shouldNotifySuccess, notification.notifySaveSuccess, shouldNotifyFailure, callBackEvent)
        : response;
    }).catch(err => {
      if (err?.errorCode === 422) throw err;
      if (shouldNotifyFailure && err) notification.notifyError(err);
      if (shouldShowLoader) hideLoader();
      return Promise.reject(err);
    });
  }

  function fetchWrapperForExport(url, content, contentType, shouldNotifyFailure = true, shouldShowLoader = false) {
    if (shouldShowLoader) showLoader();
    url = getBaseUrl(url);

    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(content),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': cookies.get("X-CSRF-TOKEN")
      }
    }).then(response => {
      extractToken(response);
      if (shouldShowLoader) hideLoader();
      return handleBlobResponse(response, 200, contentType, false, null, shouldNotifyFailure);
    }).catch(err => {
      if (err?.errorCode === 422) throw err;
      if (shouldNotifyFailure && err) notification.notifyError(err);
      if (shouldShowLoader) hideLoader();
      return Promise.reject(err);
    });
  }

  function fetchWrapperForPut(url, content, shouldNotifySuccess = true, shouldNotifyFailure = true, shouldShowLoader = false) {
    if (shouldShowLoader) showLoader();
    url = getBaseUrl(url);

    return fetch(url, {
      method: 'PUT',
      credentials: 'include',
      body: JSON.stringify(content),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': cookies.get("X-CSRF-TOKEN")
      }
    }).then(response => {
      extractToken(response);
      if (shouldShowLoader) hideLoader();
      return handleJsonResponse(response, 204, shouldNotifySuccess, notification.notifyUpdateSuccess, shouldNotifyFailure);
    }).catch(err => {
      if (err?.errorCode === 422) throw err;
      if (shouldNotifyFailure && err) notification.notifyError(err);
      if (shouldShowLoader) hideLoader();
      return Promise.reject(err);
    });
  }

  function fetchWrapperForDelete(url, shouldNotifySuccess = true, shouldNotifyFailure = true, shouldShowLoader = false) {
    if (shouldShowLoader) showLoader();
    url = getBaseUrl(url);

    return fetch(url, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': cookies.get("X-CSRF-TOKEN")
      }
    }).then(response => {
      extractToken(response);
      if (shouldShowLoader) hideLoader();
      return handleJsonResponse(response, 204, shouldNotifySuccess, notification.notifyDeleteSuccess, shouldNotifyFailure);
    }).catch(err => {
      if (shouldNotifyFailure && err) notification.notifyError(err);
      if (shouldShowLoader) hideLoader();
      return Promise.reject(err);
    });
  }

  function fetchWrapperForGet(url, shouldNotifySuccess = false, shouldNotifyFailure = true, shouldShowLoader = false) {
    if (shouldShowLoader) showLoader();
    url = getBaseUrl(url);

    return fetch(url, { credentials: 'include' }).then(response => {
      extractToken(response);
      if (shouldShowLoader) hideLoader();
      return handleJsonResponse(response, 200, shouldNotifySuccess, null, shouldNotifyFailure);
    }).catch(err => {
      if (err?.errorCode === 422) throw err;
      if (shouldNotifyFailure && err) notification.notifyError(err);
      if (shouldShowLoader) hideLoader();
      return Promise.reject(err);
    });
  }

  // Return all methods
  return {
    fetchWrapperForPost,
    fetchWrapperForPut,
    fetchWrapperForDelete,
    fetchWrapperForGet,
    handleJsonResponse
  };
}
